/* eslint-disable @typescript-eslint/naming-convention */
import {Injectable} from '@angular/core';
import {IconColor, PasswordConditions} from '@interfaces/common/common.interface';
import {IonicSafeString} from '@ionic/angular';
import {DateTime} from "luxon";

@Injectable({
  providedIn: 'root'
})
export class UtilService {

  constructor() {
  }

  transformDate(ISOdate: string): string {
    // date = 2021-10-01T00:12:48.935+03:00 // 2022-12-20T11:44:00+02:00 with new ionic 6 datetime
    const d = ISOdate.substr(0, 10);
    const t = ISOdate.substr(11, 12);
    return d + ' ' + t;
    // returned value: 2021-10-01 00:12:48.935
  }

  transformDateWithZeroTime(ISOdate: string | null): string {
    // date = 2021-11-10T11:13:44.670
    const d = ISOdate?.substr(0, 10);
    return d + 'T00:00:00.000';
    // returned value: 2021-11-10T00:00:00.000
  }

  toDateOnly(ISOdate: string | null | undefined): string | null {
    // date = 2021-11-10T11:13:44.670
    return ISOdate ? ISOdate.substring(0, 10) : null;
    // returned value: 2021-11-10
  }

  public showToast(msg: string | IonicSafeString | undefined, duration = 4000, color = 'primary') {
    this.clearToasts();
    const toast = document.createElement('ion-toast');
    toast.message = msg;
    toast.duration = duration;
    toast.color = color;
    toast.cssClass = 'mpToast';
    document.body.appendChild(toast);
    toast.present();
  }

  /**
   * The reason a failed request gives in its body, `{ success: false, error: { description } }`, or the
   * fallback when it gives none (network failure, 500, or a body in another shape).
   */
  serverErrorMessage(err: any, fallback: string): string {
    const description = err?.error?.error?.description;
    return typeof description === 'string' && description.trim().length ? description : fallback;
  }

  /**
   * A sorted copy of a table's items for its `sorted` output: numbers numerically, text case-insensitively,
   * empty values last in both directions. Sorts on `item[key]` unless `valueByKey` gives the column's value,
   * for columns whose displayed value is nested, renamed or a label for a code. Returns `items` when unsorted.
   */
  sortItems<T>(items: T[], key: string, dir: 'asc' | 'desc', valueByKey: Record<string, (item: T) => unknown> = {}): T[] {
    if (!key) {
      return items;
    }
    const valueOf = valueByKey[key] ?? ((item: T) => (item as any)[key]);
    return [...items].sort((a, b) => {
      let v1: any = valueOf(a);
      let v2: any = valueOf(b);
      if (v1 === v2) {
        return 0;
      }
      if (v1 === null || v1 === undefined) {
        return 1;
      }
      if (v2 === null || v2 === undefined) {
        return -1;
      }
      if (!isNaN(Number(v1)) && !isNaN(Number(v2))) {
        v1 = Number(v1);
        v2 = Number(v2);
      } else {
        if (typeof v1 === 'string') { v1 = v1.toLowerCase(); }
        if (typeof v2 === 'string') { v2 = v2.toLowerCase(); }
      }
      return v1 < v2 ? (dir === 'asc' ? -1 : 1) : (dir === 'asc' ? 1 : -1);
    });
  }

  passwordStrength(pass: string): string {
    if (!pass.length) {
      return '';
    }
    const mediumRegex = new RegExp('^(((?=.*[a-z])(?=.*[A-Z]))|((?=.*[a-z])(?=.*[0-9]))|((?=.*[A-Z])(?=.*[0-9])))(?=.{6,})');
    const strongRegex = new RegExp('^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})'); // /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/
    if (strongRegex.test(pass)) {
      return 'STRONG';
    } else if (mediumRegex.test(pass)) {
      return 'AVERAGE';
    } else {
      return 'WEAK';
    }
  }

  passwordValidity(password: string): PasswordConditions {
    return {
      lowercase: new RegExp('[a-z]').test(password),
      uppercase: new RegExp('[A-Z]').test(password),
      numeric: RegExp('[0-9]').test(password),
      length: password.length >= 8
    };
  }

  private clearToasts() {
    const element = document.getElementsByTagName('ion-toast');
    if (element.length) {
      for (let i = element.length - 1; i >= 0; i--) {
        element[i].parentNode!.removeChild(element[i]);
      }
    }
  }

  translateSyncDateToColor(syncDate: string | null, childDate: string): IconColor {
    if (syncDate) {
      let child = DateTime.fromISO(childDate);
      let sync = DateTime.fromISO(syncDate);
      if (child > sync) {
        return 'system-orange';
      } else {
        return 'brand-cta';
      }
    } else {
      return 'brand-light';
    }
  }
}
